import React, { useState, useEffect, useCallback } from 'react';
import 'leaflet/dist/leaflet.css';
import { routeAnalysisApi, shippingApi, shippingPriceApi, RouteAnalysis } from '../services/api';
import { ShippingRecord, Province, District } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import RouteMap from '../components/RouteMap';
import RouteLegend from '../components/RouteLegend';
import CostComparison from '../components/CostComparison';
import RoutePointsSummary from '../components/RoutePointsSummary';
import ShippingRecordsList from '../components/ShippingRecordsList';
import TollFeeLocationsList from '../components/TollFeeLocationsList';

const ShippingCostAnalysisPage: React.FC = () => {
  const { t } = useLanguage();
  const [shippingRecords, setShippingRecords] = useState<ShippingRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<ShippingRecord[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<RouteAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Route visibility toggles
  const [showActualRoute, setShowActualRoute] = useState(true);
  const [showOptimalRoute, setShowOptimalRoute] = useState(true);
  const [showTollFees, setShowTollFees] = useState(true);

  // Filters
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number>(new Date().getMonth() + 1);
  const [filterProvinceId, setFilterProvinceId] = useState<number | null>(null);
  const [filterDistrictId, setFilterDistrictId] = useState<number | null>(null);
  const [filterVehiclePlate, setFilterVehiclePlate] = useState<string>('');

  // Filter options data
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  // const [vehicles, setVehicles] = useState<Vehicle[]>([]); // Removed unused variable

  const loadFilterOptions = useCallback(async () => {
    try {
      const [provincesResponse] = await Promise.all([
  shippingPriceApi.getProvinces()
      ]);
      setProvinces(provincesResponse.data);
    } catch (err: any) {
      console.error('Error loading filter options:', err);
    }
  }, []);

  // Load shipping records with GPS data
  const loadShippingRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await shippingApi.getAll(filterYear, filterMonth);
   // Only show created records with GPS data
    const records = response.data.filter((r: any) => 
       r.status === 'created' && r.vehicleId != null
      );
   setShippingRecords(records);
  setError(null);
  } catch (err: any) {
      console.error('Error loading shipping records:', err);
 setError(err.message || 'Failed to load shipping records');
    } finally {
   setLoading(false);
 }
  }, [filterYear, filterMonth]);

  // Load districts when province filter changes
  useEffect(() => {
    const loadDistricts = async () => {
      if (filterProvinceId) {
        try {
          const response = await shippingPriceApi.getDistricts(filterProvinceId);
          setDistricts(response.data);
        } catch (err: any) {
 console.error('Error loading districts:', err);
        }
    } else {
      setDistricts([]);
        setFilterDistrictId(null);
      }
    };
    loadDistricts();
  }, [filterProvinceId]);

  const applyFilters = useCallback(() => {
    let filtered = [...shippingRecords];

    // Filter by province
  if (filterProvinceId) {
      filtered = filtered.filter(r => r.provinceId === filterProvinceId);
    }

    // Filter by district
    if (filterDistrictId) {
   filtered = filtered.filter(r => r.districtId === filterDistrictId);
 }

    // Filter by vehicle plate
    if (filterVehiclePlate) {
const searchTerm = filterVehiclePlate.toLowerCase();
      filtered = filtered.filter(r => 
        r.licensePlate?.toLowerCase().includes(searchTerm) ||
        r.vehicleLicensePlate?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredRecords(filtered);
  }, [shippingRecords, filterProvinceId, filterDistrictId, filterVehiclePlate]);

  // Load initial data
  useEffect(() => {
    loadShippingRecords();
    loadFilterOptions();
  }, [loadShippingRecords, loadFilterOptions]);

  // Apply filters whenever records or filter values change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Debug: Log when analysis changes
  useEffect(() => {
    if (analysis) {
      console.log('[ShippingCostAnalysisPage] Analysis updated:', analysis);
      console.log('[ShippingCostAnalysisPage] Toll fee locations:', analysis.tollFeeLocations);
      console.log('[ShippingCostAnalysisPage] Toll fee locations length:', analysis.tollFeeLocations?.length);
    }
  }, [analysis]);

  const analyzeRoute = async (recordId: number) => {
    setAnalyzing(true);
    setError(null);
    try {
      const response = await routeAnalysisApi.analyzeRoute(recordId);
      console.log('[ShippingCostAnalysisPage] Analysis response:', response.data);
   setAnalysis(response.data);
      setSelectedRecordId(recordId);
    } catch (err: any) {
      console.error('Error analyzing route:', err);
      setError(err.response?.data?.message || 'Failed to analyze route');
      setAnalysis(null);
    } finally {
  setAnalyzing(false);
    }
  };

  const handleClearFilters = () => {
    setFilterProvinceId(null);
    setFilterDistrictId(null);
    setFilterVehiclePlate('');
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Get unique vehicle plates from records for autocomplete suggestions
  const uniqueVehiclePlates = Array.from(new Set(
    shippingRecords
.map(r => r.licensePlate || r.vehicleLicensePlate)
      .filter(Boolean)
  )).sort();

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="bi bi-graph-up me-2"></i>
    {t('shipping.cost.analysis')}
        </h4>
      </div>

      {error && (
        <div className="alert alert-danger py-2 mb-2">
  <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
    </div>
      )}

  <div className="row">
        {/* Left Column - Filters and Shipping Records List */}
        <div className="col-lg-3">
    {/* Compact Filters */}
        <div className="card mb-2">
    <div className="card-header bg-primary bg-opacity-10 py-2">
  <h6 className="mb-0">
                <i className="bi bi-funnel me-2"></i>
     {t('filters')}
       </h6>
            </div>
    <div className="card-body p-2">
            {/* Date Filters */}
    <div className="row g-2 mb-2">
    <div className="col-6">
        <label className="form-label small mb-1">Year</label>
  <select 
    className="form-select form-select-sm" 
          value={filterYear}
           onChange={(e) => setFilterYear(parseInt(e.target.value))}
            >
        {years.map(y => (
   <option key={y} value={y}>{y}</option>
     ))}
    </select>
            </div>
      <div className="col-6">
<label className="form-label small mb-1">Month</label>
    <select 
            className="form-select form-select-sm"
           value={filterMonth}
onChange={(e) => setFilterMonth(parseInt(e.target.value))}
     >
                {months.map(m => (
        <option key={m} value={m}>{m}</option>
    ))}
       </select>
             </div>
       </div>

      {/* Destination Filters */}
  <div className="mb-2">
           <label className="form-label small mb-1">{t('province')}</label>
   <select 
   className="form-select form-select-sm"
         value={filterProvinceId || ''}
             onChange={(e) => setFilterProvinceId(e.target.value ? parseInt(e.target.value) : null)}
                >
           <option value="">{t('all')}</option>
       {provinces.map(p => (
      <option key={p.id} value={p.id}>{p.name}</option>
         ))}
       </select>
      </div>

              <div className="mb-2">
                <label className="form-label small mb-1">{t('district')}</label>
   <select 
                  className="form-select form-select-sm"
      value={filterDistrictId || ''}
               onChange={(e) => setFilterDistrictId(e.target.value ? parseInt(e.target.value) : null)}
       disabled={!filterProvinceId}
                >
        <option value="">{t('all')}</option>
                  {districts.map(d => (
        <option key={d.id} value={d.id}>{d.name}</option>
         ))}
           </select>
      </div>

    {/* Vehicle Plate Filter */}
              <div className="mb-2">
<label className="form-label small mb-1">{t('vehicle')}</label>
         <input
 type="text"
      className="form-control form-control-sm"
      placeholder={t('search') + '...'}
     value={filterVehiclePlate}
        onChange={(e) => setFilterVehiclePlate(e.target.value)}
        list="vehicle-plates-list"
  />
    <datalist id="vehicle-plates-list">
          {uniqueVehiclePlates.map(plate => (
       <option key={plate} value={plate} />
           ))}
       </datalist>
        </div>

   {/* Action Buttons */}
         <div className="d-flex gap-2">
          <button 
         className="btn btn-sm btn-primary flex-grow-1"
            onClick={loadShippingRecords}
      disabled={loading}
          >
   <i className="bi bi-arrow-clockwise me-1"></i>
       Refresh
    </button>
     {(filterProvinceId || filterDistrictId || filterVehiclePlate) && (
   <button 
        className="btn btn-sm btn-outline-secondary"
       onClick={handleClearFilters}
         title={t('clear') + ' ' + t('filters')}
>
       <i className="bi bi-x-circle"></i>
      </button>
     )}
     </div>

       {/* Filter Summary */}
    {(filterProvinceId || filterDistrictId || filterVehiclePlate) && (
       <div className="mt-2 pt-2 border-top">
   <small className="text-muted d-block">
          <i className="bi bi-info-circle me-1"></i>
        {filteredRecords.length} / {shippingRecords.length} records
          </small>
       <div className="mt-1">
     {filterProvinceId && (
     <span className="badge bg-info bg-opacity-75 me-1">
   {provinces.find(p => p.id === filterProvinceId)?.name}
          </span>
        )}
    {filterDistrictId && (
      <span className="badge bg-info bg-opacity-75 me-1">
      {districts.find(d => d.id === filterDistrictId)?.name}
             </span>
       )}
          {filterVehiclePlate && (
            <span className="badge bg-info bg-opacity-75">
                 {filterVehiclePlate}
     </span>
           )}
    </div>
    </div>
   )}
            </div>
          </div>

          {/* Shipping Records List */}
          <ShippingRecordsList
    records={filteredRecords}
  selectedRecordId={selectedRecordId}
  loading={loading}
            analyzing={analyzing}
   onSelectRecord={analyzeRoute}
      />
      </div>

        {/* Middle Column - Map and Cost Analysis */}
        <div className="col-lg-6">
          {analysis && (
     <>
          <div className="card mb-3">
<div className="card-header bg-info text-white py-2">
  <h6 className="mb-0">
       <i className="bi bi-map me-2"></i>
           {t('route.visualization')}
   </h6>
           </div>
             <div className="card-body p-0">
      <RouteMap 
           analysis={analysis}
        showActualRoute={showActualRoute}
     showOptimalRoute={showOptimalRoute}
        showTollFees={showTollFees}
                  />
                </div>
      <RouteLegend
         showActualRoute={showActualRoute}
      showOptimalRoute={showOptimalRoute}
      showTollFees={showTollFees}
  onToggleActualRoute={() => setShowActualRoute(!showActualRoute)}
                  onToggleOptimalRoute={() => setShowOptimalRoute(!showOptimalRoute)}
     onToggleTollFees={() => setShowTollFees(!showTollFees)}
   />
  </div>

              <CostComparison analysis={analysis} />
            </>
  )}

{!analysis && !analyzing && (
<div className="card">
              <div className="card-body text-center text-muted p-5">
          <i className="bi bi-cursor d-block mb-3" style={{ fontSize: '64px' }}></i>
      <h5>{t('select.shipping.record.to.analyze')}</h5>
        <p>{t('click.record.to.view.analysis')}</p>
  </div>
 </div>
          )}
        </div>

        {/* Right Column - Route Points Summary and Toll Fees */}
      <div className="col-lg-3">
      {analysis && (
   <>
           <RoutePointsSummary analysis={analysis} />
         
      {/* Always show TollFeeLocationsList component, even if empty */}
       <div className="mt-3">
   <TollFeeLocationsList 
            tollFeeLocations={analysis.tollFeeLocations || []}
        showActualRoute={showActualRoute}
  showOptimalRoute={showOptimalRoute}
      />
              </div>
         
     {/* Debug info - remove after debugging */}
              {analysis.tollFeeLocations && analysis.tollFeeLocations.length === 0 && (
      <div className="alert alert-info mt-3 py-2">
<i className="bi bi-info-circle me-2"></i>
      <strong>Debugging Info:</strong>
 <ul className="mb-0 mt-2 small">
            <li>No toll fees detected along this route</li>
       <li>Vehicle ID: {analysis.vehicleId || 'N/A'}</li>
        <li>Actual route points: {analysis.actualRoute?.routePointsCount || 0}</li>
    <li>Optimal route distance: {analysis.optimalRoute?.distanceKm.toFixed(2) || 'N/A'} km</li>
            </ul>
          <p className="mb-0 mt-2">
       <small>?? To see toll fees: Use the "Seed Toll Booths" button in Toll Fee Management</small>
        </p>
  </div>
       )}
   </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShippingCostAnalysisPage;
